import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PencilSimple, X, Check } from "@phosphor-icons/react";

/**
 * Inline "Fix WO#" pencil — shows only when the current WO# is empty or a
 * PC-xxx placeholder. One tap opens a tiny input; Enter or ✓ submits.
 *
 * Props:
 *   - jobId: the job's mongo id (or `job.id`)
 *   - currentWo: the current work_order_number string ("", "PC-2A6CC6", "3363-1", …)
 *   - onSaved: (newWo: string) => void — called after a successful save
 *   - variant?: "chip" (default, for dark WO# chip) | "row" (for admin rows)
 */
export default function InlineWoFix({ jobId, currentWo, onSaved, variant = "chip" }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const isPlaceholder = !currentWo || /^PC-[0-9A-F]{6}$/i.test((currentWo || "").trim());
  if (!isPlaceholder) return null;

  const save = async () => {
    const clean = (val || "").trim().toUpperCase();
    if (!clean) { toast.error("Type a WO# first"); return; }
    if (/^PC-/i.test(clean)) { toast.error("That's a placeholder — enter the real WO#"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/wo-number`, { work_order_number: clean });
      toast.success(`WO# set to ${r.data.work_order_number}`);
      setOpen(false); setVal("");
      onSaved?.(r.data.work_order_number);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setBusy(false); }
  };

  if (open) {
    return (
      <span
        className="inline-flex items-center gap-1 ml-1 align-middle"
        data-testid="inline-wo-fix-open"
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        <input
          autoFocus
          data-testid="inline-wo-fix-input"
          value={val}
          onChange={e => setVal(e.target.value)}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setOpen(false); setVal(""); } }}
          placeholder="e.g. 3363-1"
          disabled={busy}
          maxLength={40}
          className="h-7 px-2 text-xs font-mono font-black uppercase bg-white text-slate-900 border-2 border-cyan-500 rounded w-28 focus:outline-none focus:ring-2 focus:ring-cyan-400"
        />
        <button
          type="button"
          data-testid="inline-wo-fix-save"
          onClick={e => { e.stopPropagation(); save(); }}
          disabled={busy}
          className="h-7 w-7 inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded"
          title="Save WO#"
        ><Check size={14} weight="bold"/></button>
        <button
          type="button"
          data-testid="inline-wo-fix-cancel"
          onClick={e => { e.stopPropagation(); setOpen(false); setVal(""); }}
          disabled={busy}
          className="h-7 w-7 inline-flex items-center justify-center bg-slate-300 hover:bg-slate-400 disabled:opacity-40 text-slate-800 rounded"
          title="Cancel"
        ><X size={14} weight="bold"/></button>
      </span>
    );
  }

  // Compact pencil trigger — variant-aware styling
  const btnCls = variant === "row"
    ? "ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border border-cyan-400 rounded"
    : "ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-cyan-100 bg-cyan-800/60 hover:bg-cyan-700 rounded";

  return (
    <button
      type="button"
      data-testid="inline-wo-fix-btn"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
      className={btnCls}
      title="Enter the real WO# (this one is a placeholder)"
    >
      <PencilSimple size={11} weight="bold"/> Fix WO#
    </button>
  );
}
